import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import RecordingContext from "../../context/RecordingContext";
import { NavFrame } from "./frames/navframe";
import SideFrame from "./frames/sideframe";
import SummaryTabWrap from "./tabs/summary/summarytabwrap";
import LabsWrap from "./tabs/labs/labsWrap";
import Procedures from "./tabs/procedure/procedure";
import PriorVisit from "./tabs/priorvisits/priorvisit";
import Vitals from "./tabs/vitals/vitals";
import RPM from "./tabs/rpm/rpm";
import Imaging from "./tabs/imaging/imaging";
import { Form } from "./tabs/forms/form";
import Document from "./tabs/Document/document";
import dayjs from "dayjs";
import CCmTable from "./tabs/ccm/ccm";
import api from "../../api/api";
import Refillstab from "../../orders/tabs/refills";
import ChatWrapper from "../../header/chat/chatwrapper";
import { eventBus } from "../../utils/eventBus";
import Editscreen from "../../blinkscribe/archeivescreens/editscreen";
import { recordingTargetFor, useRecordingTargetState } from "../../utils/recordingTarget";
import ChartRecordingNudge from "./ChartRecordingNudge";
import PulseChat from "./frames/PulseChat";
import { pulseActionTab, pulseRefTab } from "./frames/pulseRefTab";
import blinkscribeapi from "../../api/blinkscribeapi";
import Threads from "./tabs/nonvisitnotes/threads";
import CareIQTab from "./tabs/careiq/CareIQTab";
import CareIQTabN3 from "./tabs/careiq/CareIQTabN3";
import { bsAccessGated, careiqTabN3Only, chartDefaultTab, chartOrdersPanelShown, chartTab, getPracticeId, pulseChatShown } from "../../config/practice";
import { useBsAccess } from "../../utils/bsAccess";
import "./frames/chartframe.css";
import { useCareiqN3Chart } from "../../utils/careiqN3Flag";
import { clearChartStartTab, peekChartStartTab } from "../../utils/chartStartTab";

// a module that mocks RecordingContext without its default export (tests): no recording state
const NO_RECORDING = React.createContext(null);

// a phone-sized chart window (the shell stacks the patient panel over the tab below 768 px)
const phoneScreen = () => {
  try {
    return Boolean(window.matchMedia && window.matchMedia("(max-width: 767px)").matches);
  } catch (_) {
    return false;
  }
};

const PateintsChartsWrapper = ({data ,dob,onClose,fetchpcarddata,setFetchedData, isloaderactive,fetchedData,searchdate, careiqN3Flag}) => {
  // CareIQ N3 rollout flag for this chart window ("n3" | "legacy"):
  // the chart modal (pmodal) reads it when the chart opens and passes it in;
  // a wrapper mounted on its own reads it here. It decides only the CareIQ
  // tab (case 11) and its content box below; the chart shell, sidebar and top
  // bar are the same with or without it (N3's old-frame classes and the
  // <1024 px compact strip were dropped on the redesigned shell, 10-06).
  const ownN3Flag = useCareiqN3Chart(careiqN3Flag === undefined);
  const n3Flag = careiqN3Flag === undefined ? ownN3Flag : careiqN3Flag;
  // This chart's CareIQ flag as the tab rules take it (config/practice.js
  // chartTabAllowed / chartDefaultTab / chartTab): in 7825 the CareIQ tab
  // exists only for "n3" and an "n3" chart opens on it; 9928 ignores it.
  const n3Chart = n3Flag === "n3";
  const n3ChartRef = useRef(n3Chart);
  n3ChartRef.current = n3Chart;
  // CareIQ is the default chart tab (Summary retired — matches care-sandbox);
  // a practice with a chart tab allowlist opens on its first tab (7825: Vitals;
  // CareIQ for an "n3" login). Where the CareIQ tab is N3-only (7825) the
  // button that opened the chart may ask for its tab (Intake / Vitals: Vitals;
  // utils/chartStartTab.js), so an "n3" chart opened by Intake never mounts
  // the CareIQ tab (and its fetches) on the way to Vitals. 9928 ignores it.
  const [currentTab, setCurrentTabRaw] = useState(() => {
    const start = careiqTabN3Only() ? peekChartStartTab(data?.patientid) : null;
    return start != null ? chartTab(start, n3Chart) : chartDefaultTab(11, n3Chart);
  });
  // the request is read once: by this chart, when it mounts
  useEffect(() => {
    if (careiqTabN3Only()) clearChartStartTab();
  }, []);
  // Every tab change goes through the practice's allowlist (config/practice.js
  // chartTab): a tab it leaves out opens the chart's first tab instead. 9928
  // has no list, so this is setCurrentTabRaw itself. (The ref: the event
  // listeners below keep the first render's setCurrentTab.)
  const setCurrentTab = useCallback((tab) => setCurrentTabRaw(chartTab(tab, n3ChartRef.current)), []);
  // The end-of-visit order review (components/OrderReview; providers whose BlinkScribe payload carries
  // review.enabled): when a recording of this patient with the review on stops and is being saved, the chart
  // goes to the CareIQ tab, which opens the review screen once the final suggestions land. Everyone else:
  // nothing changes.
  const recCtx = useContext(RecordingContext || NO_RECORDING) || {};
  const recNow = Boolean(recCtx.isGlobalRecording);
  const recProcessing = Boolean(recCtx.isProcessing);
  const recV2 = recCtx.orderSuggestionsV2;
  const reviewArmedRef = useRef(false);
  useEffect(() => {
    const mine = Boolean(recV2 && recV2.review && recV2.review.enabled === true
      && data?.patientid != null && String(recV2.patientid) === String(data.patientid));
    if (recNow) {
      reviewArmedRef.current = mine;
      return;
    }
    if (reviewArmedRef.current && recProcessing) {
      reviewArmedRef.current = false;
      if (n3ChartRef.current) setCurrentTab(11);
    }
  }, [recNow, recProcessing, recV2, data?.patientid, setCurrentTab]);
  const [pendingResults, setPendingResults] = useState([]);
  const[internalnav,setInternaNav]=useState(false)
  const [vitalsinternav,setVitalsInternav]=useState(0)
  const[isreocrdingdisabled,setIsRecordingDisabled]=useState(false)
    const [hasRpmAccess, setHasRpmAccess] = useState(false);
    // Note editor opened from LiveScribePanel's "Edit" button (via eventBus).
    const [editRecord, setEditRecord] = useState(null);
    // Whether today's appointment already has a note — suppresses the nudge.
    const [hasExistingNote, setHasExistingNote] = useState(false);
    // Pulse chart-assistant panel, toggled from the nav bar avatar.
    const [pulseOpen, setPulseOpen] = useState(false);
    // A Pulse "pull up the lipid trend" action: Labs opens focused on this
    // analyte ({ analyte, panel, seq }; seq re-applies the same analyte).
    const [labFocus, setLabFocus] = useState(null);
    // Chart chrome (top bar): patient panel rail vs expanded, and the
    // CareIQ timeline panel. With the N3 flag a phone (< 768 px) opens the
    // chart on the patient strip (the rail as one row above the tabs under a line naming the patient,
    // chartframe.css .n3-shell): the expanded panel filled the whole first
    // screen and the CareIQ tab started below it (responsive audit 10-07).
    // The strip keeps record / note / level / dx one tap away; its arrow
    // opens the full panel. Everyone else: as before (expanded).
    const [sidebarCollapsed, setSidebarCollapsed] = useState(() => n3Flag === "n3" && phoneScreen());
    const [showTimeline, setShowTimeline] = useState(true);
    // OSA batch 6 (7825): no recording nudge for a user known to have no
    // BlinkScribe in the practice (9928 never checks).
    const bsAccess = useBsAccess();
    const nudgeHidden = bsAccessGated() && bsAccess === "no";
    // The visit a recording from this chart goes to (sidebar mic, note editor).
    const recordingTargetState = useRecordingTargetState();
    const recTarget = recordingTargetFor(data, recordingTargetState);
    const practiseid = getPracticeId();
      const [chat,setChat]=useState(false)
const audiotabchange=()=>{
setInternaNav(1)
setCurrentTab(5)

}
const formatDate = (dateString) => {
  return dayjs(dateString).format("YYYY-MM-DD");
};

useEffect(() => {
  const handleNavigateToPriorVisit = (payload) => {
    if (payload?.tab !== undefined) {
      setCurrentTab(payload.tab);
    }
    if (payload?.internalTab !== undefined) {
      setInternaNav(payload.internalTab);
    }
  };

  eventBus.on("navigateToPriorVisit", handleNavigateToPriorVisit);

  return () => {
    eventBus.off("navigateToPriorVisit", handleNavigateToPriorVisit);
  };
}, []);

// LiveScribePanel's "Edit" button emits openNoteEditor; without this listener
// the click does nothing at all (the event had no subscriber in cloud2).
useEffect(() => {
  const handleOpenNoteEditor = (payload) => {
    if (payload?.recordingId) {
      setEditRecord({ id: payload.recordingId });
    }
  };
  eventBus.on("openNoteEditor", handleOpenNoteEditor);
  return () => { eventBus.off("openNoteEditor", handleOpenNoteEditor); };
}, []);

// Does today's appointment already have a note? Drives the recording nudge so
// a provider who has already documented the visit isn't nagged. Re-runs when a
// recording finishes (refreshDashboard).
useEffect(() => {
  if (!data?.patientid) return;
  let cancelled = false;
  const fetchHasNote = async () => {
    try {
      const t = new Date();
      const iso = `${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, "0")}-${String(t.getDate()).padStart(2, "0")}`;
      const res = await blinkscribeapi.post(
        `/v1/patient/${data.patientid}/recording/details`,
        { filter_array: { date_filter: { start_date: iso, end_date: iso } } }
      );
      if (cancelled) return;
      const records = res?.data || res || [];
      const apptId = data?.appointmentid;
      setHasExistingNote(Array.isArray(records) && records.some((r) => {
        if (!r) return false;
        if (apptId && r.config_appointmentid &&
            String(r.config_appointmentid) !== String(apptId)) return false;
        return r.transcriptionstatus === "Summarized" || r.transcriptionstatus === "APPROVED";
      }));
    } catch (_) {
      // Non-blocking: keep the previous value rather than nagging because of a
      // transient API failure.
    }
  };
  fetchHasNote();
  eventBus.on("refreshDashboard", fetchHasNote);
  return () => {
    cancelled = true;
    eventBus.off("refreshDashboard", fetchHasNote);
  };
}, [data?.patientid, data?.appointmentid]);
useEffect(() => {
  const handleNavigateTovitals= (payload) => {
    if (payload?.tab !== undefined) {
      setCurrentTab(payload.tab);
    }
    if (payload?.internalTab !== undefined) {
      setVitalsInternav(payload.internalTab);
    }
  };

  eventBus.on("navigateToPriorVitals", handleNavigateTovitals);

  return () => {
    eventBus.off("navigateToPriorVitals", handleNavigateTovitals);
  };
}, []);

// Listen for thread notification navigation
useEffect(() => {
  const handleNavigateToThreads = (payload) => {
    if (payload?.tab !== undefined) {
      setCurrentTab(payload.tab);
    }
  };

  eventBus.on("navigateToThreadsTab", handleNavigateToThreads);

  return () => {
    eventBus.off("navigateToThreadsTab", handleNavigateToThreads);
  };
}, []);

const fetchuserrpm=async()=>{
  try {
    const response=await api.get(`/v1/${practiseid}/patient/${data?.patientid}/rpm/get-rpm-patient`)
  
    if(response.status===200){
setHasRpmAccess(response?.rpm_status||false);
    }else{
      setHasRpmAccess(false);
    }
  } catch (error) {
    console.log(error)
  }
}
useEffect(()=>{
if(data?.patientid){
  fetchuserrpm()
}
},[data])

  const renderContent = () => {
    switch (currentTab) {

      case 0:
        return <SummaryTabWrap isloaderactive={isloaderactive}dataId={data} setFetchedData={setFetchedData}
             fetchedData={fetchedData} searchdate={formatDate(data?.startdate)} />;
      case 1:
        return <LabsWrap dataId={data} focus={labFocus} onFocusClear={() => setLabFocus(null)} />;
      case 2:
        return <Document dataId={data} searchdate={formatDate(data?.startdate)} />;
        case 3:
          return <Vitals vitalsinternav={vitalsinternav} setVitalsInternav={setVitalsInternav} dataId={data} setFetchedData={setFetchedData} />;
          case 4:
            return hasRpmAccess ? <RPM dataId={data} /> : null;
        case 5:
          return <PriorVisit internavtab={internalnav} dataId={data} searchdate={formatDate(data?.startdate)} />;
        
        case 6: 
        return <Imaging data={{ 
  ...data, 
  ...(dob && { dob }) 
}}

           />
           case 7:
            return <Form  
            data={data}
            searchdate={formatDate(data?.startdate)}
            />

            case 8:
              return <CCmTable setIsRecordingDisabled={setIsRecordingDisabled}  data={data}
            searchdate={formatDate(data?.startdate)}/>
            case 9:
              return <div className="max-h-[80vh] overflow-auto">


                  <Refillstab
                                    data={data}
                                    // setShowOrders={setShowOrders}
                                    // appointmentId={appointmentId}
                                    startDate={formatDate(data?.startdate)}
                                  />
              </div>

              case 10:
                return <div className="max-h-[75vh] overflow-auto">
<Threads  data={data} />

                </div> 
              case 11:
                // CareIQ N3 for a login with the flag; the legacy tab, untouched,
                // for everyone else and as the instant rollback (9928). A
                // practice whose CareIQ tab is N3-only (7825) never renders the
                // legacy tab (chartTab already keeps a non-"n3" chart off 11).
                if (n3Flag !== "n3" && careiqTabN3Only()) return null;
                return n3Flag === "n3"
                  ? <CareIQTabN3 dataId={data} onSwitchTab={setCurrentTab} />
                  : <CareIQTab dataId={data} onSwitchTab={setCurrentTab} setPendingResults={setPendingResults} showTimeline={showTimeline} onCloseTimeline={() => setShowTimeline(false)} onToggleTimeline={() => setShowTimeline((v) => !v)} />;
      default:
        return null;
    }
  };

  return (

<div className={`bmch-shell${n3Flag === "n3" ? " n3-shell" : ""} flex flex-col md:flex-row w-full`}>

        <div className={`bmch-sidecol${sidebarCollapsed ? " rail" : ""}`}>
          <SideFrame
            onClose={onClose}
            setChat={setChat}
            fetchpcarddata={fetchpcarddata}
            currentTab={currentTab}
            isreocrdingdisabled={isreocrdingdisabled}
            data={data}
            audiotabchange={audiotabchange}
            setFetchedData={setFetchedData}
            fetchedData={fetchedData}
            collapsed={sidebarCollapsed}
            onToggleCollapse={() => setSidebarCollapsed((v) => !v)}
          />
        </div>
        {/* min-w-0: without it this flex child refuses to shrink below its
            content width and the whole chart scrolls sideways on tablets. */}
        <div className="bmch-maincol flex-grow min-w-0">
          <NavFrame
            activeTab={currentTab}
            onTabClick={(index) => setCurrentTab(index)}
            hasRpmAccess={hasRpmAccess}
            careiqN3={n3Chart}
            pulseOpen={pulseOpen}
            setPulseOpen={setPulseOpen}
            onClose={onClose}
            // N3 chart window (pmodal passed the flag) below 1024 px: the Orders
            // toggle is this top-bar button instead of a pill floating over the
            // tab's content (it covered the rows' controls, responsive audit
            // 10-07); pmodal listens for chartToggleOrders. Hidden from 1024 px,
            // where the window's vertical Orders tab stays.
            onOrders={careiqN3Flag === "n3" && chartOrdersPanelShown() ? () => eventBus.emit("chartToggleOrders") : undefined}
            ordersBelowLgOnly
          />
          {/* N3 (flag) keeps the padded box (its old frame's p-4) and scrolls
              its own root at >= 1024 px (CareIQTabN3.css). */}
          <div className={`bmch-content${currentTab === 11 ? (n3Flag === "n3" ? " n3-tabbox" : " flush") : ""}`}>
            {renderContent()}

          </div>
        </div>        {chat?<ChatWrapper setChat={setChat} patientdata={data} flow={'comment'} />:<></>}
        {pulseOpen && pulseChatShown(n3Chart) && (
          <PulseChat
            patientId={data?.patientid}
            patientName={data?.patientname || data?.patient_name ||
                         `${data?.firstname || ""} ${data?.lastname || ""}`.trim()}
            onClose={() => setPulseOpen(false)}
            careiqN3={n3Chart}
            onRefClick={(ref) => {
              // Route a citation click to the tab that shows it (care's tab
              // ids; a tab this chart does not list falls back to CareIQ).
              const tab = pulseRefTab(ref, n3Chart);
              if (tab != null) setCurrentTab(tab);
            }}
            onAction={(action) => {
              // "Pull up" actions: a lab trend opens Labs focused on the
              // analyte; open_tab / open_document move to that tab (the
              // document itself opens in Pulse's viewer). A tab this chart
              // does not list falls back to CareIQ, else stays put.
              const tab = pulseActionTab(action, n3Chart);
              if (tab === 4 && !hasRpmAccess) return;
              if (action?.type === "open_lab_trend" && tab === 1) {
                setLabFocus((prev) => ({ analyte: action.analyte, panel: action.panel || null, seq: (prev?.seq || 0) + 1 }));
              }
              if (tab != null) setCurrentTab(tab);
            }}
          />
        )}
        {/* Recording reminder for today's appointment. Emits
            startRecordingFromNudge, which sideframe listens for. Not for a
            user known to have no BlinkScribe in the practice (7825). */}
        {!nudgeHidden && (
        <ChartRecordingNudge
          data={data}
          hasExistingNote={hasExistingNote}
          onStartRecording={() => eventBus.emit("startRecordingFromNudge")}
        />
        )}
        {/* Note editor, opened by LiveScribePanel's Edit button. Editscreen
            portals its own fullscreen overlay to <body> (no wrapper here —
            a second overlay double-dimmed and, inside this transformed
            subtree, anchored to the wrong box). Closed by setIsEditScreen(false). */}
        {editRecord && (
          <Editscreen
            selectedRecord={editRecord}
            // The visit this chart is open for. Editscreen warns + asks before
            // any write when the opened note belongs to a different visit
            // (a save there re-posts to THAT visit's Athena encounter).
            // (the recording target: the live visit today or the selected
            // visit, utils/recordingTarget.js; else the card's, as before)
            chartAppointmentId={recTarget.row?.appointmentid ?? data?.appointmentid}
            setIsEditScreen={(v) => { if (!v) setEditRecord(null); }}
          />
        )}
      </div>

  );
};

export default PateintsChartsWrapper;
